/**
 * Approval Content Schema — Sprint 2 (Stateful Workflow Platform).
 *
 * Authoritative Zod schemas for the `agent_tool_approvals.content_schema_jsonb`
 * column added in Sprint 1. This shape drives the schema-driven auto-renderer
 * that ships in Sprint 2 (§8.4) plus the hand-designed exception renderers
 * that ship in Sprint 10+ (§8.5).
 *
 * Authority:
 *   - docs/STATEFUL-WORKFLOW-PLATFORM-SPEC.md §8.3 (canonical shape)
 *   - docs/STATEFUL-WORKFLOW-PLATFORM-SPEC.md §8.4 (auto-renderer)
 *   - docs/STATEFUL-WORKFLOW-PLATFORM-SPEC-v1.1-AMENDMENTS.md
 *       Q6  — `customer_match` displayType ships in Sprint 2
 *       Q9  — `line_items` field type GATED to Sprint 12 (NOT accepted here)
 *
 * Rule §5.4 #26 — Zod is the source of truth. Types are derived via z.infer.
 */

import { z } from 'zod';
import { ExportControlSchema, SensitivitySchema } from './document.schema.js';

// ─── Path safety (FIX-J / P0-1) ──────────────────────────────────────────────
//
// Dotted JSON-Pointer-ish paths used by FieldDef, AiContextRef and
// ContextPanelFieldRef are written into the prompt template AND walked at
// runtime by setPath/getPath in routes/v1/agent-approvals-edit.ts. Without
// strict validation here, a workflow author (or compromised agent) could
// declare `path: '__proto__.isAdmin'` and any later setPath() call would
// mutate Object.prototype process-wide.
//
// Defense-in-depth:
//   1. SAFE_SEGMENT_RE forbids __proto__, constructor, prototype as a
//      whole segment; identifier-style segments only.
//   2. PATH_SAFE_RE composes segments with `.` separators.
//   3. agent-approvals-edit.ts:setPath/getPath also throw on forbidden
//      segments (belt-and-suspenders for ANY caller that bypasses Zod).
//
// The leading-`@` form (`@context`) is rejected — workflow authors are
// expected to use identifier-style names; the `:`-form used in spec §8.3
// examples (`entity:customer.preferred_terms`) is *one* segment and
// matches because the regex permits `:` inside a segment.

/** Segments matching this regex are forbidden anywhere in a path. */
const FORBIDDEN_SEGMENTS = new Set([
  '__proto__',
  'constructor',
  'prototype',
]);

/**
 * One dotted-path segment. Identifier-style + `$` + `:` (for the
 * `entity:customer` shape). Max 80 chars per segment so a single 200-char
 * path can have at least 2–3 segments and the total stays cheap to store.
 */
const SAFE_SEGMENT_RE =
  /^[A-Za-z_$][A-Za-z0-9_$:-]{0,79}$/;

/**
 * Full dotted path. One or more SAFE_SEGMENTs joined by `.`. Total length
 * still bounded to 200 by the caller's `.max(200)` so the JSONB column
 * stays small.
 */
const PATH_SAFE_RE =
  /^[A-Za-z_$][A-Za-z0-9_$:-]{0,79}(?:\.[A-Za-z_$][A-Za-z0-9_$:-]{0,79})*$/;

/**
 * Zod string schema for any FieldDef-style path. Enforces:
 *   - length 1..200
 *   - identifier-style segments separated by `.`
 *   - no `__proto__` / `constructor` / `prototype` segments
 *
 * Exported so any future schema that carries a runtime-walked path can
 * reuse the same gate (e.g. ContextPanel.dataPath).
 */
export const SafeDottedPathSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(PATH_SAFE_RE, {
    message:
      'Path must be dotted identifier segments (e.g. "content.draft.body"). ' +
      'Bracket syntax, leading separators and special chars are not allowed.',
  })
  .refine(
    (s) => {
      for (const seg of s.split('.')) {
        if (FORBIDDEN_SEGMENTS.has(seg)) return false;
      }
      return true;
    },
    {
      message:
        'Path segments cannot be "__proto__", "constructor", or "prototype" (prototype pollution guard).',
    },
  );

// ─── Classification (FIX-J / P1) ─────────────────────────────────────────────
//
// Per-field optional 2-axis classification, mirroring
// classification-floor.ts. The Sprint 9 AI-edit endpoint reads these to
// drop aiContext paths the caller cannot view BEFORE the prompt is built.

export const FieldClassificationSchema = z
  .object({
    sensitivity: SensitivitySchema
      .default('unclassified'),
    exportControl: ExportControlSchema.default('none'),
  })
  .strict();
export type FieldClassification = z.infer<typeof FieldClassificationSchema>;

// ─── Field types ─────────────────────────────────────────────────────────────
//
// `line_items` is DELIBERATELY omitted from the V1 enum. The auto-renderer
// cannot serviceably render a line-items grid; it ships in Sprint 12 alongside
// the hand-designed `purchase_order` renderer. Workflow authors who declare
// type='line_items' before Sprint 12 hit a design-time validation error here
// and must fall back to type='json' with a warning banner (per Q9 amendment).

export const FieldTypeSchema = z.enum([
  // Primitives the auto-renderer can render with `<EditableField />`:
  'text',
  'multiline',
  'rich_text',
  'number',
  'currency',
  'date',
  'datetime',
  'email',
  'phone',
  'url',
  'select',
  'multiselect',
  'checkbox',
  'recipients',
  'classification',
  'json',
  // NOTE: 'line_items' deliberately omitted — gated to Sprint 12 per v1.1 Q9.
  // NOTE: 'customer_match' is a DISPLAY TYPE, not a field type; it lives in
  //       DisplayTypeSchema below.
]);
export type FieldType = z.infer<typeof FieldTypeSchema>;

// ─── Display types ───────────────────────────────────────────────────────────
//
// Per Sprint 2 plan, only `generic` (auto-rendered) is universally available.
// `email_draft` is the V1 hand-designed renderer (this PR). `customer_match`
// is a Sprint 2 inclusion per v1.1 Q6 — minimal renderer ships here, polish
// in Sprint 12. The remaining hand-designed renderers ship in Sprint 12.

export const DisplayTypeSchema = z.enum([
  'generic', // auto-renderer fallback
  'email_draft', // hand-designed, Sprint 2 (V1)
  'customer_match', // Sprint 2 minimal renderer (v1.1 Q6); polish in Sprint 12
  'purchase_order', // Sprint 12
  'crm_response', // Sprint 12
  'document_review', // Sprint 12
  'access_change', // Sprint 12
]);
export type DisplayType = z.infer<typeof DisplayTypeSchema>;

// ─── Validation rules ────────────────────────────────────────────────────────

export const FieldValidationSchema = z
  .object({
    required: z.boolean().optional(),
    minLength: z.number().int().nonnegative().optional(),
    maxLength: z.number().int().positive().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    pattern: z.string().max(500).optional(), // serialized RegExp source
  })
  .strict();
export type FieldValidation = z.infer<typeof FieldValidationSchema>;

// ─── Field definition ────────────────────────────────────────────────────────
//
// `path` is a dotted JSON-Pointer-ish reference into the approval's `evidence`
// (or `decided_evidence` once the user starts editing). Bounded to 200 chars
// so storing the schema in JSONB stays cheap.

export const SelectOptionSchema = z
  .object({
    value: z.string().min(1).max(200),
    label: z.string().min(1).max(200),
  })
  .strict();
export type SelectOption = z.infer<typeof SelectOptionSchema>;

export const AiContextRefSchema = z
  .object({
    // FIX-J / P0-1 — prototype-pollution guard. Uses SafeDottedPathSchema.
    path: SafeDottedPathSchema,
    label: z.string().min(1).max(200),
    /**
     * FIX-J / P1 — per-aiContext classification. The Sprint 9 AI-edit
     * endpoint compares this against the caller's clearance and drops
     * paths the caller cannot read BEFORE the prompt is built. Defaults
     * to unclassified+none when omitted (i.e. no gate).
     */
    classification: FieldClassificationSchema.optional(),
  })
  .strict();
export type AiContextRef = z.infer<typeof AiContextRefSchema>;

export const FieldDefSchema = z
  .object({
    // FIX-J / P0-1 — prototype-pollution guard. Uses SafeDottedPathSchema.
    path: SafeDottedPathSchema,
    label: z.string().min(1).max(200),
    type: FieldTypeSchema,
    aiEditable: z.boolean().default(false),
    description: z.string().max(500).optional(),
    validation: FieldValidationSchema.optional(),
    /**
     * Optional context the Sprint 9 AI-edit endpoint should resolve and inject
     * into the LLM prompt when the user edits this field via AIEditBox.
     */
    aiContext: z.array(AiContextRefSchema).max(20).optional(),
    /**
     * Required when type === 'select' or 'multiselect'. The auto-renderer
     * passes these to the shared `<EditableField />` primitive.
     */
    selectOptions: z.array(SelectOptionSchema).max(200).optional(),
    /**
     * FIX-J / P1 — per-field classification (spec §6 v1.1). Optional;
     * when omitted, the field is treated as unclassified+none.
     */
    classification: FieldClassificationSchema.optional(),
  })
  .strict()
  .superRefine((field, ctx) => {
    if (
      (field.type === 'select' || field.type === 'multiselect') &&
      (!field.selectOptions || field.selectOptions.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Field type '${field.type}' requires selectOptions`,
        path: ['selectOptions'],
      });
    }
  });
export type FieldDef = z.infer<typeof FieldDefSchema>;

// ─── Context panels ──────────────────────────────────────────────────────────
//
// Right-rail panels in the auto-renderer (CRM card, thread, doc refs, JSON,
// AI reasoning chain). The auto-renderer renders these as collapsible cards.

export const ContextPanelFieldRefSchema = z
  .object({
    // FIX-J / P0-1 — prototype-pollution guard. Uses SafeDottedPathSchema.
    path: SafeDottedPathSchema,
    label: z.string().min(1).max(200),
    type: FieldTypeSchema.optional(),
  })
  .strict();
export type ContextPanelFieldRef = z.infer<typeof ContextPanelFieldRefSchema>;

export const ContextPanelKindSchema = z.enum([
  'fields', // generic label/value pairs (default)
  'crm_card',
  'thread',
  'doc_refs',
  'chain',
  'json',
  'iframe',
]);
export type ContextPanelKind = z.infer<typeof ContextPanelKindSchema>;

export const ContextPanelSchema = z
  .object({
    panelId: z.string().min(1).max(60),
    title: z.string().min(1).max(200),
    kind: ContextPanelKindSchema.default('fields'),
    collapsible: z.boolean().default(true),
    collapsedByDefault: z.boolean().default(false),
    /**
     * For kind='fields': list of paths to render as label/value pairs.
     * For other kinds: the auto-renderer looks at `dataPath` instead.
     */
    fields: z.array(ContextPanelFieldRefSchema).max(50).default([]),
    /**
     * Root path into evidence for non-`fields` panel kinds.
     * Example: kind='thread' + dataPath='content.previous_emails'
     *
     * FIX-J / P0-1 — prototype-pollution guard. Uses SafeDottedPathSchema.
     */
    dataPath: SafeDottedPathSchema.optional(),
  })
  .strict();
export type ContextPanel = z.infer<typeof ContextPanelSchema>;

// ─── Actions ─────────────────────────────────────────────────────────────────
//
// Footer-bar buttons. The auto-renderer's <ActionBar /> primitive wires
// `shortcut` into a keyboard listener and dispatches `decision` through the
// existing approve/reject/edit-approve mutations in use-agent-approvals.ts.

export const ActionVariantSchema = z.enum([
  'primary',
  'secondary',
  'danger',
  'ghost',
]);
export type ActionVariant = z.infer<typeof ActionVariantSchema>;

export const ActionDecisionSchema = z.enum([
  'approve',
  'reject',
  'custom', // future-proofing for Sprint 9+ extended decisions
]);
export type ActionDecision = z.infer<typeof ActionDecisionSchema>;

export const ActionSchema = z
  .object({
    id: z.string().min(1).max(60),
    label: z.string().min(1).max(60),
    variant: ActionVariantSchema.default('secondary'),
    shortcut: z.string().max(40).optional(),
    decision: ActionDecisionSchema,
    /** Optional confirmation dialog before the action fires. */
    confirmation: z
      .object({
        title: z.string().min(1).max(200),
        body: z.string().min(1).max(500),
        confirmLabel: z.string().min(1).max(60),
      })
      .strict()
      .optional(),
  })
  .strict();
export type Action = z.infer<typeof ActionSchema>;

// ─── Top-level schema ────────────────────────────────────────────────────────
//
// This is the canonical shape stored at agent_tool_approvals.content_schema_jsonb.
// The auto-renderer parses this via .safeParse and falls back to a generic
// JSON-viewer when parsing fails (defense-in-depth — workflow authors can
// emit malformed schemas, the UI must not crash).

export const ApprovalContentSchemaSchema = z
  .object({
    displayType: DisplayTypeSchema,
    version: z.literal(1),
    editableFields: z.array(FieldDefSchema).max(50),
    contextPanels: z.array(ContextPanelSchema).max(20).default([]),
    actions: z.array(ActionSchema).min(1).max(10),
  })
  .strict();
export type ApprovalContentSchema = z.infer<typeof ApprovalContentSchemaSchema>;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Resolve a dotted path against an arbitrary record. Used by the auto-renderer
 * to read editable-field values out of `evidence` / `decided_evidence`.
 *
 * Tolerant by design: a missing intermediate key returns `undefined` rather
 * than throwing. The UI renders the field with an empty value.
 *
 * FIX-J / P0-1 — refuses forbidden segments defensively even when callers
 * skip Zod validation.
 */
export function readPath(
  source: Record<string, unknown> | null | undefined,
  path: string,
): unknown {
  if (!source || typeof source !== 'object') return undefined;
  const parts = path.split('.');
  for (const part of parts) {
    if (FORBIDDEN_SEGMENTS.has(part)) return undefined;
  }
  let current: unknown = source;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Immutably write a value at a dotted path. Used by the auto-renderer when
 * the user edits an EditableField — the parent collects the new value and
 * the manual-edit / ai-edit endpoint persists it to decided_evidence.
 *
 * Intermediate objects are created as needed. Arrays at intermediate paths
 * are preserved (we cannot infer whether `.foo.0.bar` means "index 0 of an
 * array" without schema introspection — Sprint 2 does not edit through arrays).
 *
 * FIX-J / P0-1 — throws on forbidden segments. Defense-in-depth for ANY
 * caller that bypasses Zod (e.g. legacy code paths that hand-build paths).
 */
export function writePath(
  source: Record<string, unknown> | null | undefined,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const root: Record<string, unknown> = source ? { ...source } : {};
  const parts = path.split('.');
  if (parts.length === 0) return root;
  for (const part of parts) {
    if (FORBIDDEN_SEGMENTS.has(part)) {
      throw new Error(
        `writePath: refusing forbidden segment "${part}" in path "${path}" (prototype pollution guard).`,
      );
    }
  }

  let cursor: Record<string, unknown> = root;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    const next = cursor[key];
    if (next && typeof next === 'object' && !Array.isArray(next)) {
      cursor[key] = { ...(next as Record<string, unknown>) };
    } else {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[parts[parts.length - 1]] = value;
  return root;
}
